import '../../sass/Testimonials.scss'
import EmblaCarousel from 'embla-carousel'
import {estimateReadingTimeInMS} from './testimonials'

export function throttle(func, limit) {
    let lastFunc;
    let lastRan;

    return function (...args) {
        const context = this;

        if (!lastRan) {
            func.apply(context, args);
            lastRan = Date.now();
        } else {
            clearTimeout(lastFunc);
            lastFunc = setTimeout(function () {
                if ((Date.now() - lastRan) >= limit) {
                    func.apply(context, args);
                    lastRan = Date.now();
                }
            }, limit - (Date.now() - lastRan));
        }
    };
}

const timeouts = new Map()
const emblaApiMap = new Map()
const emblaDescriptions = new Map()
const emblaSliderActiveElement = new Map()
const fadeOutStarted = new Map()

const onClickCallback = (index, slideIndex) => (
    () => {
        stopEmblaAutoplay(index)
        const emblaApi = emblaApiMap.get(index)
        const slideNodes = emblaApi.slideNodes()
        const previousActiveElement = emblaSliderActiveElement.get(index)
        const slideItem = slideNodes[slideIndex]

        if (slideNodes[previousActiveElement]) {
            slideNodes[previousActiveElement].classList.remove('Active')
        }
        slideItem.classList.add('Active')

        const prevDescriptionItem = emblaDescriptions.get(index)?.[previousActiveElement]
        if (prevDescriptionItem) {
            prevDescriptionItem.classList.remove('Active')
        }

        const descriptionItem = emblaDescriptions.get(index)?.[slideIndex]
        if (descriptionItem) {
            descriptionItem.classList.add('Active')
        }

        emblaSliderActiveElement.set(index, slideIndex)
        startEmblaAutoplay(index, emblaApi)
    }
)

function stopEmblaAutoplay(index) {
    const timeout = timeouts.get(index)
    if (timeout) {
        clearTimeout(timeout)
        timeouts.set(index, undefined)
    }
}

function startEmblaAutoplay(index, emblaApi) {
    stopEmblaAutoplay(index)

    const activeSlide = emblaSliderActiveElement.get(index)
    const duration = Math.max(4000, estimateReadingTimeInMS(emblaDescriptions.get(index)[activeSlide].innerText))
    let timeout
    if (emblaApi.canScrollPrev() && emblaApi.canScrollNext()) {
        timeout = setTimeout(() => {
            emblaApi.scrollNext()
        }, duration)
    } else {
        const maxSlides = emblaApi.slideNodes().length - 1
        let nextSlide = activeSlide + 1
        if (nextSlide > maxSlides) {
            nextSlide = 0
        }

        timeout = setTimeout(() => {
            onClickCallback(index, nextSlide)()
        }, duration)
    }

    timeouts.set(index, timeout)
}

function sortBasedOnSnap(array, snapIndex) {
    let newArray = []
    const endArray = []
    let firstSlide = undefined;
    let lastSlide = undefined;

    array.forEach(item => {
        if (item < snapIndex) {
            endArray.push(item)

            lastSlide = item
        } else {
            newArray.push(item)
        }
    })

    newArray = [
        ...newArray,
        ...endArray,
    ];

    if (newArray.length > 0) {
        firstSlide = newArray.slice(0, 1)?.[0]
        lastSlide = newArray.slice(-1)?.[0]
    }

    return {
        firstSlide,
        lastSlide,
        slidesInView: newArray,
    }
}

function resetEmblaAutoplay(index, emblaApi) {
    stopEmblaAutoplay(index)

    fadeOutStarted.set(index, false)
    let activeSlide = emblaSliderActiveElement.get(index)
    // emblaDescriptions.get(index)
    const {firstSlide, lastSlide, slidesInView} = sortBasedOnSnap(emblaApi.slidesInView(), emblaApi.selectedScrollSnap())
    if (slidesInView.indexOf(activeSlide) === -1) {
        onClickCallback(index, firstSlide)()
    }

    startEmblaAutoplay(index, emblaApi)
}

let observer = undefined
const InViewportCallback = entries => {
    for (let entry of entries) {
        const el = entry.target
        const index = el.dataset?.index
        const loaded = el.dataset?.loaded === 'true'

        const onScrollCallback = (api) => {
            if (!fadeOutStarted.get(index) && api.canScrollNext() && api.canScrollPrev()) {
                stopEmblaAutoplay(index)

                const emblaApi = emblaApiMap.get(index)
                fadeOutStarted.set(index, true)

                const activeSlide = emblaSliderActiveElement.get(index)
                const descriptions = emblaDescriptions.get(index)

                emblaApi.slideNodes()[activeSlide].classList.remove('Active')
                descriptions[activeSlide].classList.remove('Active')
            }
        }

        const onSlideInView = throttle((api) => {
            const slidesInView = api.slidesInView()
            if (slidesInView.length) {
                const {firstSlide} = sortBasedOnSnap(slidesInView, api.selectedScrollSnap())
                const activeElement = emblaSliderActiveElement.get(index)

                //if (activeElement !== firstSlide) {
                    onClickCallback(index, firstSlide)()
                //}
            }
        }, 50)

        if (entry.isIntersecting) {
            let emblaApi = emblaApiMap.get(index)

            if (!loaded) {
                fadeOutStarted.set(index, false)
                emblaApi.on('scroll', onScrollCallback)

                emblaApi.on('slidesInView', onSlideInView)

                emblaApi.on('settle', (api) => {
                    resetEmblaAutoplay(index, api)
                })

                el.dataset.loaded = 'true'
            }

            requestAnimationFrame(() => {
                resetEmblaAutoplay(index, emblaApi)
            })
        } else {
            if (index && loaded) {
                stopEmblaAutoplay(index)
                const emblaApi = emblaApiMap.get(index)

                emblaApi.off('scroll', onScrollCallback)

                emblaApi.off('slidesInView', onSlideInView)

                emblaApi.off('settle', (api) => {
                    resetEmblaAutoplay(index, api)
                })

                el.dataset.loaded = 'false'
            }
        }
    }
}

const emblaOptions = {
    align: 'start',
    slidesToScroll: 1,
    loop: true,
    inViewThreshold: 0.95,
    duration: 20,
    delay: 0,
}

export function createObserver(headerHeight) {
    if (observer)
        observer.disconnect()

    observer = new IntersectionObserver(InViewportCallback, {
        root: null,
        rootMargin: `-${headerHeight}px 0px 0px 0px`,
        threshold: 0.95,
    })

    const emblaNodes = document.getElementsByClassName('Partners--Items')
    let i = 0
    for (let emblaNode of emblaNodes) {
        const index = String(i)
        if (emblaNode.dataset.index !== index) {
            emblaNode.dataset.index = index
        }

        let emblaApi = emblaApiMap.get(index)
        if (!emblaApi) {
            emblaApi = EmblaCarousel(emblaNode, emblaOptions)
            emblaApiMap.set(index, emblaApi)
            emblaSliderActiveElement.set(index, 0)

            const Descriptions = emblaApi.rootNode().querySelectorAll('.Descriptions .Item')
            let descriptionIndex = 0
            for (let Description of Descriptions) {
                if (descriptionIndex > 0) {
                    Description.style.transform = `translateX(${descriptionIndex * -100}%) translateZ(0)`;
                }

                ++descriptionIndex
            }
            emblaDescriptions.set(index, Descriptions)

            let slideIndex = 0
            const slideNodes = emblaApi.slideNodes()
            for (let slideItem of slideNodes) {
                slideItem.addEventListener('click', onClickCallback(index, slideIndex), {passive: true})
                slideItem.addEventListener('mouseenter', onClickCallback(index, slideIndex), {passive: true})

                ++slideIndex
            }
        }

        observer.observe(emblaNode)
        ++i
    }
}
